import React, { useCallback } from 'react';
import { FieldLabel } from '@puckeditor/core';
import type { CustomField } from '@puckeditor/core';

import { fieldControlStyle } from './field-control-style';

type UrlFieldProps = {
  field: { label?: string };
  name: string;
  value: string | null | undefined;
  onChange: (value: string) => void;
  readOnly?: boolean;
};

const inputStyle: React.CSSProperties = {
  ...fieldControlStyle,
};

const hintStyle: React.CSSProperties = {
  marginTop: 4,
  fontSize: 12,
  color: '#646d72',
};

/**
 * A custom URL text field, built to fix a specific click-test disclosure gap on Image's Link URL
 * box (ECOMM-12193): `link.tsx`'s `href` is Puck's native `text` field with
 * `defaultProps.href = '#'`, so the box arrives PRE-FILLED and visually reads as "already has a
 * value" even before an author touches it. Image's `linkHref` is the same native `text` type with
 * `defaultProps.linkHref = ''`, so it arrives blank instead -- correctly, since an empty
 * `linkHref` is what actually means "no link" in `image.tsx`'s render (see `ImageLink`); copying
 * Link's `'#'` default onto Image would wrap every image in a dead `<a href="#">` by default,
 * which is worse than the cosmetic inconsistency. But a blank box with no placeholder and no
 * helper text gives an author nothing to distinguish "optional, deliberately blank" from
 * "required, not yet filled in" -- Puck's native `text` field exposes neither a `placeholder` nor
 * a hint. This widget is the fix: a plain URL text input that LOOKS optional (via `placeholder`)
 * and SAYS it's optional (via `hint`), without changing what an empty value means.
 *
 * `hint` follows the same rendered-helper-text convention `selectField()` established in
 * `select-field.tsx` -- small muted text under the control, wired up via `aria-describedby` --
 * so this reads as a sibling widget rather than inventing a fourth visual language for helper
 * copy in this directory.
 */
export function UrlField(placeholder?: string, hint?: string) {
  return function UrlFieldWidget({ field, name, value, onChange, readOnly }: UrlFieldProps): JSX.Element {
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (readOnly) return;
        onChange(e.currentTarget.value);
      },
      [onChange, readOnly]
    );
    const hintId = hint ? `url-hint-${name}` : undefined;

    // Saved page data is unvalidated JSON -- a `null` (or otherwise non-string) saved value must
    // not be handed to a controlled <input>'s `value` prop, which would make React treat it as an
    // uncontrolled input and warn on the first keystroke that flips it back to controlled.
    const currentValue = typeof value === 'string' ? value : '';

    return (
      <FieldLabel label={field.label ?? name} readOnly={readOnly}>
        <input
          type='text'
          value={currentValue}
          onChange={handleChange}
          disabled={readOnly}
          placeholder={placeholder}
          data-testid={`url-input-${name}`}
          aria-describedby={hintId}
          style={inputStyle}
        />
        {hint && (
          <p id={hintId} data-testid={`url-hint-${name}`} style={hintStyle}>
            {hint}
          </p>
        )}
      </FieldLabel>
    );
  };
}

export type UrlFieldOptions = {
  placeholder?: string;
  hint?: string;
  /** LLM-facing copy, distinct from `hint` -- see `select-field.tsx`'s doc comment for why the
   * two must stay separate rather than reusing one string for both audiences. */
  instructions?: string;
  /** Passed through to `ai.stream`; defaults to `false`, matching `linkHref`'s prior native-field
   * setting -- a URL has no useful intermediate streamed state. */
  stream?: boolean;
};

/**
 * Builds a complete Puck `custom` URL field, including the `ai.schema` the AI plugin requires for
 * every `custom` field. Mirrors `selectField()`/`altTextField()`'s guard: a bare
 * `{ type: 'custom', render: UrlField(...) }` works in the editor UI but 400s the entire AI chat
 * request the moment the AI plugin reaches it ("Missing schema for ... with custom field type") --
 * asserted codebase-wide by `findCustomFieldsMissingAiSchema` (see
 * `standalone-page/puck-config.test.tsx`) -- so the schema is baked in here rather than left to
 * each call site to remember.
 */
export function urlField(label: string, options: UrlFieldOptions = {}): CustomField<string> {
  const { placeholder, hint, instructions, stream = false } = options;
  return {
    type: 'custom',
    label,
    render: UrlField(placeholder, hint),
    ai: {
      schema: { type: 'string' },
      stream,
      ...(instructions ? { instructions } : {}),
    },
  };
}
